import { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useIsUngatedForHelpDeskRoutingScopeFiltering, useIsUngatedForTeamsOnly } from '../../auth/hooks/useGates';
import { HELPDESK } from '../../shared/constants/workspaces';
import { TrackUsageProvider } from '../../usage-tracking/contexts/TrackUsageContext';
import { ROUTING_RULE_TYPES } from '../constants/RoutingTypes';
import { DISTRIBUTIONS } from '../constants/distributions';
import { isValid } from '../operators/isValid';
import { checkCanHaveFallback, checkHasTeamsOnly, checkHasUsersAndTeams, checkIsRoutingRulesType, getDistribution } from '../operators/routingRulesGetters';
import { setDistribution, setTeamsOnly, setUsersAndTeams } from '../operators/routingRulesSetters';
import { getChannelRoutingDistribution } from '../utils/distribution';
import AssignToAvailableCheckbox from './AssignToAvailableCheckbox';
import { CustomerAgentRow } from './CustomerAgentRow';
import { DistributionSelect } from './DistributionSelect';
import FallbackRow from './FallbackRow';
import RoundRobinRow from './RoundRobinRow';
import RoutingRulesSelectRow from './RoutingRulesSelectRow';
import TeamsOnlyRow from './TeamsOnlyRow';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const RoutingRulesWrapper = styled.div.withConfig({
  displayName: "RoutingRules__RoutingRulesWrapper",
  componentId: "sc-1mxd072-0"
})(["width:100%;"]);
export const RoutingRules = ({
  aiChatbotId,
  canSelectDistribution,
  disabled,
  disableKeepAssigneeOption,
  FormControl,
  hasEditAccess,
  hasRoutingAccess,
  hideAvailableUserCheckbox,
  inboxId,
  objectTypeId,
  onOpenChange,
  onRuleUpdate: onRuleUpdateProp,
  overrides = {},
  routingRules,
  routingSource,
  showAnyUserOption,
  showKeepAssigneeOption,
  showOwnerOption = true,
  showSelectionRow = true,
  showTeamsOnlyOption,
  showUnassignedOption = true,
  trackUsage = () => {},
  usageTracker,
  use,
  userAndTeamSelectValueComponent,
  workSpace
}) => {
  const isUngatedForScopeFiltering = useIsUngatedForHelpDeskRoutingScopeFiltering();
  //In the future, we will be removing hasEditAcccess and hasRoutingAccess in favor of user just providing this as part of the 'disabled' prop.
  const lacksScope = isUngatedForScopeFiltering && workSpace === HELPDESK ? !hasEditAccess : !hasRoutingAccess;
  const isDisabled = disabled || lacksScope;
  const isUngatedForTeamsOnly = useIsUngatedForTeamsOnly();
  const shouldShowTeamsOnlyOption = isUngatedForTeamsOnly && showTeamsOnlyOption;
  const usersAndTeamsSelectError = !isValid(routingRules);
  const defaultDistribution = getChannelRoutingDistribution(use);
  const showFallbackRow = checkCanHaveFallback(routingRules);
  const showRoundRobinRow = checkHasUsersAndTeams(routingRules);
  const showTeamsRow = checkHasTeamsOnly(routingRules);
  const showDistributionRow = canSelectDistribution && defaultDistribution === DISTRIBUTIONS.RANDOM;
  const showCustomerAgentRow = checkIsRoutingRulesType(routingRules, ROUTING_RULE_TYPES.CUSTOMER_AGENT);
  const setChannelDistribution = useMemo(
  // this reduces calls to `getChannelRoutingDistribution` and also
  // allows for better typing then call `setDistribution(distribution, routingRules)`
  () => setDistribution(defaultDistribution), [defaultDistribution]);
  const onRuleUpdate = useMemo(() => {
    if (showDistributionRow) {
      return onRuleUpdateProp;
    }
    return newRoutingRules => onRuleUpdateProp(setChannelDistribution(newRoutingRules));
  }, [onRuleUpdateProp, setChannelDistribution, showDistributionRow]);
  const onChange = useCallback((newUserIds, newTeamIds) => {
    // we do a non-null assertion because this can only be called by
    // `RoundRobinRow` which only shows when the routing rules have users and teams or teams only
    // meaning routingRules is defined
    if (showTeamsRow) {
      onRuleUpdate(setTeamsOnly(routingRules, newTeamIds));
    } else {
      onRuleUpdate(setUsersAndTeams(routingRules, newUserIds, newTeamIds));
    }
  }, [onRuleUpdate, routingRules, showTeamsRow]);
  const onTeamsOnlyChange = useCallback(newTeamIds => {
    // Teams-only specific change handler
    onRuleUpdate(setTeamsOnly(routingRules, newTeamIds));
  }, [onRuleUpdate, routingRules]);
  return /*#__PURE__*/_jsx(TrackUsageProvider, {
    tracker: usageTracker,
    children: /*#__PURE__*/_jsxs(RoutingRulesWrapper, {
      children: [showSelectionRow && /*#__PURE__*/_jsx(RoutingRulesSelectRow, {
        aiChatbotId: aiChatbotId !== null && aiChatbotId !== void 0 ? aiChatbotId : undefined,
        disabled: isDisabled,
        disableKeepAssigneeOption: disableKeepAssigneeOption,
        FormControl: FormControl,
        overrides: overrides.routingTypeSelect,
        showAnyUserOption: showAnyUserOption,
        showKeepAssigneeOption: showKeepAssigneeOption,
        showTeamsOnlyOption: shouldShowTeamsOnlyOption,
        showUnassignedOption: showUnassignedOption,
        showOwnerOption: showOwnerOption,
        routingRules: routingRules,
        onOpenChange: onOpenChange,
        onRuleUpdate: onRuleUpdate,
        routingSource: routingSource,
        isUngatedForHelpDeskRoutingScopeFiltering: isUngatedForScopeFiltering
      }), showFallbackRow && /*#__PURE__*/_jsx(FallbackRow, {
        disabled: isDisabled,
        FormControl: FormControl,
        routingRules: routingRules,
        onRuleUpdate: onRuleUpdate,
        showTeamsOnly: shouldShowTeamsOnlyOption,
        isUngatedForHelpDeskRoutingScopeFiltering: isUngatedForScopeFiltering
      }), showTeamsRow && /*#__PURE__*/_jsx(TeamsOnlyRow, {
        disabled: isDisabled,
        inboxId: inboxId,
        isUngatedForHelpDeskRoutingScopeFiltering: isUngatedForScopeFiltering,
        objectTypeId: objectTypeId,
        onTeamSelect: onTeamsOnlyChange,
        routingRules: routingRules,
        routingSource: routingSource,
        trackUsage: trackUsage,
        teamsSelectError: usersAndTeamsSelectError,
        workSpace: workSpace
      }), showRoundRobinRow && /*#__PURE__*/_jsx(RoundRobinRow, {
        disabled: isDisabled,
        FormControl: FormControl,
        inboxId: inboxId,
        isUngatedForHelpDeskRoutingScopeFiltering: isUngatedForScopeFiltering,
        objectTypeId: objectTypeId,
        onUserOrTeamSelect: onChange,
        routingRules: routingRules,
        routingSource: routingSource,
        showLabel: false,
        trackUsage: trackUsage,
        userAndTeamSelectValueComponent: userAndTeamSelectValueComponent,
        usersAndTeamsSelectError: usersAndTeamsSelectError,
        workSpace: workSpace
      }), showCustomerAgentRow && /*#__PURE__*/_jsx(CustomerAgentRow, {
        routingSource: routingSource
      }), (showRoundRobinRow || showTeamsRow) && /*#__PURE__*/_jsxs(_Fragment, {
        children: [showDistributionRow && /*#__PURE__*/_jsx(DistributionSelect, {
          channelType: use,
          disabled: isDisabled,
          distribution: getDistribution(routingRules),
          onChange: newDistribution => {
            onRuleUpdateProp(
            // this is ok to do because for this to show we know that
            // `routingRules` is defined
            setDistribution(newDistribution)(routingRules));
          }
        }), !hideAvailableUserCheckbox && /*#__PURE__*/_jsx(AssignToAvailableCheckbox, {
          channelType: use,
          disabled: isDisabled,
          objectTypeId: objectTypeId,
          onRuleUpdate: onRuleUpdate,
          routingRules: routingRules
        })]
      })]
    })
  });
};
RoutingRules.displayName = 'RoutingRules';
export default RoutingRules;